// src/components/chatUnreadStore.js
//
// Single source of truth for project-chat unread badge counts.
//
// Compatible with BOTH:
//   - useSyncExternalStore (Hive) — requires getState() snapshot pattern
//   - useState + subscribe (People) — subscribers call getCounts() directly
//
// KEY DESIGN:
//   getState() always returns a fresh snapshot object with a NEW reference
//   every time the store mutates. useSyncExternalStore checks reference equality
//   to decide whether to re-render — a new object = re-render triggered.
//
//   getCounts() is kept for the People-app useState pattern.

const _s = {
  unreadCounts:  {},
  openProjectId: null,
  _subscribers:  [],
  _snapshot:     { unreadCounts: {}, openProjectId: null },
};

// ── SNAPSHOT ─────────────────────────────────────────────
// Called after every mutation. Builds a NEW object so
// useSyncExternalStore detects the change via reference inequality.
function _buildSnapshot() {
  _s._snapshot = {
    unreadCounts:  { ..._s.unreadCounts },
    openProjectId: _s.openProjectId,
  };
}

// ── NOTIFY ────────────────────────────────────────────────
// Rebuilds snapshot FIRST, then notifies subscribers with no args.
// useSyncExternalStore subscribers call getState() to get new snapshot.
// People-app useState subscribers call getCounts() directly.
function _notify() {
  _buildSnapshot();
  _s._subscribers.forEach(fn => {
    try { fn(); } catch (e) { console.error('chatUnreadStore:', e); }
  });
}

// ── READ ──────────────────────────────────────────────────
// getState() — for useSyncExternalStore (Hive).
// Returns same reference if nothing changed, new reference after mutation.
export function getState() {
  return _s._snapshot;
}

// getCounts() — for People-app useState pattern.
export function getCounts() {
  return { ..._s.unreadCounts };
}

export function getOpenProjectId() {
  return _s.openProjectId;
}

// ── WRITE ─────────────────────────────────────────────────
export function incrementUnread(projectId, hasMention = false) {
  const key = String(projectId);
  if (_s.openProjectId === key) return;
  const prev = _s.unreadCounts[key] || { count: 0, hasMention: false };
  _s.unreadCounts = {
    ..._s.unreadCounts,
    [key]: { count: prev.count + 1, hasMention: prev.hasMention || hasMention },
  };
  _notify();
}

export function seedCounts(baseline) {
  if (!baseline || typeof baseline !== 'object') return;
  // Ignore empty baselines — don't overwrite real-time data with nothing
  if (Object.keys(baseline).length === 0) return;
  _s.unreadCounts = { ...baseline };
  _notify();
}

export function clearProject(projectId) {
  const key = String(projectId);
  if (!_s.unreadCounts[key]) return;
  const { [key]: _removed, ...rest } = _s.unreadCounts;
  _s.unreadCounts = rest;
  _notify();
}

export function setOpenProject(projectId) {
  _s.openProjectId = projectId ? String(projectId) : null;
  if (_s.openProjectId && _s.unreadCounts[_s.openProjectId]) {
    const { [_s.openProjectId]: _removed, ...rest } = _s.unreadCounts;
    _s.unreadCounts = rest;
  }
  _notify();
}

export function setClosedProject() {
  _s.openProjectId = null;
  _notify();
}

export const clearOpenProject = setClosedProject;

// ── SUBSCRIBE ─────────────────────────────────────────────
// useSyncExternalStore calls subscribe(callback) and expects
// a cleanup function. The callback is invoked with NO args.
export function subscribe(callback) {
  _s._subscribers.push(callback);
  return () => {
    _s._subscribers = _s._subscribers.filter(fn => fn !== callback);
  };
}

// ── LEGACY COMPAT ─────────────────────────────────────────
export const chatUnreadStore = {
  getState, getCounts, getOpenProjectId,
  incrementUnread, seedCounts,
  clearProject, setOpenProject, setClosedProject, clearOpenProject,
  subscribe,
};